import { useState, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { orderBy } from 'lodash-es';
import { Order, OrderGroup } from '@/types';
import React from 'react';

export enum SortBy {
    UnitName,
    Created,
    Category,
}

export type SortDirection = 'asc' | 'desc';

export interface SortState {
    id: SortBy;
    direction: SortDirection;
}

export interface SortDefinition {
    id: SortBy;
    labelGenerator: () => JSX.Element;
    sortFunc: (data: any[], direction: SortDirection) => any[];
}

export interface UseOrderSortingResult {
    sortDefinitions: SortDefinition[];
    currentSort: SortState;
    setCurrentSort: (sort: SortState) => void;
    currentSortDefinition: SortDefinition | undefined;
    applySorting: <T>(data: T[]) => T[];
}

/**
 * Custom hook for managing order sorting state and logic.
 * Can be used for both Order[] and OrderGroup[] arrays.
 */
export function useOrderSorting(): UseOrderSortingResult {
    const sortDefinitions: SortDefinition[] = useMemo(
        () => [
            {
                id: SortBy.UnitName,
                labelGenerator: () => <Trans>Unit name</Trans>,
                sortFunc: (data: any[], direction: SortDirection) => {
                    // Handle both Order[] and OrderGroup[] cases
                    if (data.length > 0 && 'residentialUnitName' in data[0]) {
                        // Direct sorting for items with residentialUnitName property
                        return orderBy(data, 'residentialUnitName', direction);
                    }
                    // Fallback for other structures
                    return orderBy(data, 'residentialUnitName', direction);
                },
            },
            {
                id: SortBy.Created,
                labelGenerator: () => <Trans>Created</Trans>,
                sortFunc: (data: any[], direction: SortDirection) => {
                    if (data.length === 0) return data;

                    // Handle OrderGroup[] case
                    if ('orders' in data[0] && Array.isArray(data[0].orders)) {
                        return orderBy(data, (group: OrderGroup) => new Date(group.orders[0]?.createDate || 0), direction);
                    }

                    // Handle Order[] case
                    if ('createDate' in data[0]) {
                        return orderBy(data, (order: Order) => new Date(order.createDate), direction);
                    }

                    return data;
                },
            },
            {
                id: SortBy.Category,
                labelGenerator: () => <Trans>Category</Trans>,
                sortFunc: (data: any[], direction: SortDirection) => {
                    if (data.length === 0) return data;

                    // Handle OrderGroup[] case
                    if ('orders' in data[0] && Array.isArray(data[0].orders)) {
                        return orderBy(
                            data,
                            (group: OrderGroup) => {
                                const firstOrder = group.orders[0];
                                const firstLine = firstOrder?.orderLines[0];
                                return firstLine?.itemCategoryId || 0;
                            },
                            direction
                        );
                    }

                    // Handle Order[] case
                    if ('orderLines' in data[0] && Array.isArray(data[0].orderLines)) {
                        return orderBy(
                            data,
                            (order: Order) => {
                                const firstLine = order.orderLines[0];
                                return firstLine?.itemCategoryId || 0;
                            },
                            direction
                        );
                    }

                    return data;
                },
            },
        ],
        []
    );

    const initSortObject: SortState = {
        id: SortBy.Created,
        direction: 'desc',
    };

    const [currentSort, setCurrentSort] = useState<SortState>(initSortObject);

    const currentSortDefinition = useMemo(
        () => sortDefinitions.find((x) => x.id === currentSort?.id),
        [sortDefinitions, currentSort?.id]
    );

    const applySorting = useMemo(() => {
        return <T,>(data: T[]): T[] => {
            if (!data || !currentSortDefinition) return data;
            return currentSortDefinition.sortFunc(data, currentSort.direction) as T[];
        };
    }, [currentSortDefinition, currentSort.direction]);

    return {
        sortDefinitions,
        currentSort,
        setCurrentSort,
        currentSortDefinition,
        applySorting,
    };
}
